import { createContext, useCallback, useContext, useMemo } from "react";

import { GreetingContextType, GreetingProviderProps } from "../../types";

const GreetingContext = createContext<GreetingContextType | undefined>(undefined);

export const GreetingProvider = ({
    children,
    greetingFields,
    activeDialogIndex,
    setActiveDialogIndex,
    handleAddSentence,
    handleRemoveSentence,
    handleRemoveGreetingGroup,
    updateGreetings,
    isUpdatingKioskSetting,
    unsavedCards,
    markCardAsChanged,
    clearCardUnsaved,
    uploadingCards,
    markCardAsUploading,
    clearCardUploading,
}: GreetingProviderProps) => {
    // Memoized dialog operations for better API readability
    const openDialog = useCallback(
        (index: number) => {
            setActiveDialogIndex(index);
        },
        [setActiveDialogIndex]
    );

    const closeDialog = useCallback(() => {
        setActiveDialogIndex(null);
    }, [setActiveDialogIndex]);

    // Derived state
    const isDialogOpen = activeDialogIndex !== null;
    const isAnyCardUploading = uploadingCards.size > 0;

    const value = useMemo(
        () => ({
            greetingFields,
            activeDialogIndex,
            isDialogOpen,
            openDialog,
            closeDialog,
            handleAddSentence,
            handleRemoveSentence,
            handleRemoveGreetingGroup,
            updateGreetings,
            isUpdatingKioskSetting,
            unsavedCards,
            markCardAsChanged,
            clearCardUnsaved,
            uploadingCards,
            markCardAsUploading,
            clearCardUploading,
            isAnyCardUploading,
        }),
        [
            greetingFields,
            activeDialogIndex,
            isDialogOpen,
            openDialog,
            closeDialog,
            handleAddSentence,
            handleRemoveSentence,
            handleRemoveGreetingGroup,
            updateGreetings,
            isUpdatingKioskSetting,
            unsavedCards,
            markCardAsChanged,
            clearCardUnsaved,
            uploadingCards,
            markCardAsUploading,
            clearCardUploading,
            isAnyCardUploading,
        ]
    );

    return <GreetingContext.Provider value={value}>{children}</GreetingContext.Provider>;
};

export const useGreeting = () => {
    const context = useContext(GreetingContext);
    if (context === undefined) {
        throw new Error("useGreeting must be used within a GreetingProvider");
    }
    return context;
};
